/* eslint-disable */

import { AuctionManager } from '@/services/auctionManager'
import { TRAINING_TYPES } from '@/models/chocobos'
import { NemesisManager } from '@/services/nemesisOwnerManager'
import { EffectManager } from '@/services/effectManager'


export class NPCOwnerManager {
  static processPostRaceActions(gameStore, options = {}) {
    const { skipHeavyOperations = false } = options
    
    // Skip heavy operations during transit to prevent hanging
    if (skipHeavyOperations) {
      // Only do essential operations during transit
      this.processDailyTraining(gameStore)
      return []
    }
    
    const nemeses = gameStore.owners.filter(owner => owner.isNemesis)
    nemeses.forEach(nemesis => {
      NemesisManager.processNemesisActions(gameStore, nemesis)
    })

    this.processTrainingDecisions(gameStore)
    this.processDailyTraining(gameStore)
    return this.processAuctionAndSales(gameStore)
  }

  static processTrainingDecisions(gameStore) {
    const nonPlayerOwners = gameStore.owners.filter(owner => !owner.isPlayer)
    
    nonPlayerOwners.forEach(owner => {
      const ownedChocobos = gameStore.getChocobosByOwnerId(owner.id)
      
      ownedChocobos.forEach(chocobo => {
        const newFocus = this.decideTrainingFocus(chocobo, owner)
        if (newFocus !== chocobo.trainingType) {
          chocobo.setTrainingFocus(newFocus)
        }
      })
    })
  }

  static processDailyTraining(gameStore) {
    const DAILY_RANCH_XP = 100;
    
    // Process all owners including player
    gameStore.owners.forEach(owner => {
      const ownedChocobos = gameStore.getChocobosByOwnerId(owner.id)
      if (ownedChocobos.length === 0) return

      // Calculate base XP per chocobo, rounding up to ensure minimum 1 XP
      const baseXpPerChocobo = Math.ceil(DAILY_RANCH_XP / ownedChocobos.length)

      // Apply XP to each chocobo with effects
      ownedChocobos.forEach(chocobo => {
        if (chocobo.level < chocobo.maxLevel) {
          let finalXP = baseXpPerChocobo
          
          // Apply effect multipliers for player chocobos
          if (owner.isPlayer) {
            const xpMultiplier = EffectManager.calculateXPMultiplier(chocobo, gameStore.currentDay)
            const trainingMultiplier = EffectManager.calculateTrainingMultiplier(chocobo, chocobo.trainingType, gameStore.currentDay)
            const ageTrainingModifier = chocobo.getAgeTrainingModifier(gameStore.currentDay)
            finalXP = Math.floor(baseXpPerChocobo * xpMultiplier * trainingMultiplier * ageTrainingModifier)
          } else {
            // Apply age-based training modifier for NPCs too
            const ageTrainingModifier = chocobo.getAgeTrainingModifier(gameStore.currentDay)
            finalXP = Math.floor(baseXpPerChocobo * ageTrainingModifier)
          }
          
          chocobo.gainExperience(finalXP)
        }
      })
    })
  }

  static processNPCTrading(gameStore) {
    const activeAuctions = gameStore.activeAuctions
    const nonPlayerOwners = gameStore.owners.filter(owner => !owner.isPlayer)
    const trades = []

    activeAuctions.forEach(auction => {
      // Skip if seller is player or chocobo already sold
      if (auction.owner.isPlayer || !auction.owner.chocoboIds.includes(auction.chocobo.id)) {
        return
      }

      // Find potential NPC buyers
      const potentialBuyers = nonPlayerOwners.filter(buyer => 
        buyer.id !== auction.owner.id && // Not the seller
        buyer.money >= auction.basePrice && // Can afford it
        buyer.chocoboIds.length < 15 && // Has space
        this.isChocoboDesirable(auction.chocobo, buyer, gameStore) // Actually wants this chocobo
      )

      if (potentialBuyers.length > 0) {
        // Sort buyers by their interest and ability to pay
        const rankedBuyers = potentialBuyers
          .map(buyer => ({
            buyer,
            interest: this.calculateBuyerInterest(auction.chocobo, buyer, auction.basePrice),
            maxBid: this.calculateMaxBid(buyer, auction.basePrice)
          }))
          .filter(b => b.interest > 0.7) // Only seriously interested buyers
          .sort((a, b) => b.interest - a.interest)

        if (rankedBuyers.length > 0) {
          const topBuyer = rankedBuyers[0]
          const finalPrice = Math.min(topBuyer.maxBid, 
            Math.round(auction.basePrice * (1 + (topBuyer.interest * 0.2))))

          trades.push({
            auction,
            buyer: topBuyer.buyer,
            price: finalPrice
          })
        }
      }
    })

    // Execute the trades
    trades.forEach(trade => {
      this.executeNPCTrade(trade.auction, trade.buyer, trade.price, gameStore)
    })

    return trades
  }

  static decideTrainingFocus(chocobo, owner) {
    const scores = {
      [TRAINING_TYPES.SPEED]: 0,
      [TRAINING_TYPES.STAMINA]: 0,
      [TRAINING_TYPES.BEHAVIOR]: 0
    }

    // Factor 1: X-Factors (highest priority)
    if (chocobo.hasSpeedXFactor) scores[TRAINING_TYPES.SPEED] += 3
    if (chocobo.hasStaminaXFactor) scores[TRAINING_TYPES.STAMINA] += 3

    // Factor 2: Current stats relative to level
    const expectedStatValue = chocobo.level * 2
    const speedDeficit = expectedStatValue - chocobo.speed
    const staminaDeficit = expectedStatValue - chocobo.stamina
    
    scores[TRAINING_TYPES.SPEED] += speedDeficit > 0 ? 2 : 0
    scores[TRAINING_TYPES.STAMINA] += staminaDeficit > 0 ? 2 : 0

    // Factor 3: Race history analysis (if available)
    if (chocobo.raceHistory && chocobo.raceHistory.length > 0) {
      const recentRaces = chocobo.raceHistory.slice(-5)
      
      recentRaces.forEach(race => {
        if (race && race.raceType) {
          // Add weighting based on race type performance
          if (race.raceType === 'short') scores[TRAINING_TYPES.SPEED] += 0.5
          if (race.raceType === 'long') scores[TRAINING_TYPES.STAMINA] += 0.5
        }
      })
    }

    // Factor 4: Behavior considerations
    if (chocobo.behaviorId > 3) {
      scores[TRAINING_TYPES.BEHAVIOR] += 4 - Math.floor(owner.shrewdness / 25)
    }

    // Factor 5: Owner trait influences
    if (owner.competitiveness > 70) scores[TRAINING_TYPES.SPEED] += 1
    if (owner.shrewdness > 70) scores[TRAINING_TYPES.STAMINA] += 1

    // Return the training type with highest score
    return Object.entries(scores)
      .reduce((a, b) => a[1] > b[1] ? a : b)[0]
  }

  static processAuctionAndSales(gameStore) {
    const notifications = []
    const newAuctions = this.generateNPCAuctions(gameStore)
    
    // Process NPC-to-NPC trades first
    const npcTrades = this.processNPCTrading(gameStore)
    
    if (npcTrades.length > 0) {
      notifications.push({
        type: 'npc_trades',
        trades: npcTrades,
        message: `${npcTrades.length} chocobo${npcTrades.length > 1 ? 's were' : ' was'} traded between ranches.`
      })
    }

    // Add new auctions to store
    if (newAuctions.length > 0) {
      gameStore.addAuctions(newAuctions)
      notifications.push({
        type: 'auctions',
        auctions: newAuctions,
        message: `${newAuctions.length} new chocobo${newAuctions.length > 1 ? 's have' : ' has'} been listed for auction.`
      })
    }

    // NOTE: Bidding system moved to BidManager for proper persistence
    // Old notification-based bidding is disabled in favor of persistent bid management  

    return notifications
  }

  static isChocoboDesirable(chocobo, buyer, gameStore) {
    const ownedChocobos = gameStore.getChocobosByOwnerId(buyer.id)
    
    // Don't buy if already owns similar or better chocobos
    const hasSimilar = ownedChocobos.some(owned => 
      owned.color === chocobo.color &&
      owned.level >= chocobo.level &&
      owned.speed >= chocobo.speed &&
      owned.stamina >= chocobo.stamina
    )
    if (hasSimilar) return false

    let score = 0
    
    // Value rare colors more
    if (chocobo.color === 'gold') score += 30
    if (chocobo.color === 'black') score += 20
    if (['blue', 'green'].includes(chocobo.color)) score += 10

    // Value X-Factors highly
    if (chocobo.hasSpeedXFactor) score += 25
    if (chocobo.hasStaminaXFactor) score += 25

    // Value good behavior
    if (chocobo.behaviorId <= 3) score += 15

    // Consider owner traits
    if (buyer.competitiveness > 70 && (chocobo.speed > 100 || chocobo.stamina > 100)) {
      score += 20
    }
    if (buyer.ambition > 70 && chocobo.level > 20) {
      score += 15
    }

    // Consider owner's focus
    const ownedHighLevels = ownedChocobos.filter(c => c.level >= 20).length
    if (buyer.competitiveness > 70 && ownedHighLevels < 3 && chocobo.level >= 20) {
      score += 20
    }

    return score >= 40 // Threshold for desirability
  }

  static calculateBuyerInterest(chocobo, buyer, basePrice) {
    let interest = 1.0

    // Financial capacity affects interest
    const wealthFactor = buyer.money / (basePrice * 3)
    interest *= Math.min(1.5, Math.max(0.5, wealthFactor))

    // Traits affect interest
    if (buyer.competitiveness > 70 && (chocobo.speed > 100 || chocobo.stamina > 100)) {
      interest *= 1.2
    }
    if (buyer.ambition > 70 && chocobo.level > 20) {
      interest *= 1.15
    }
    if (buyer.shrewdness > 70) {
      interest *= 0.9 // More cautious about purchases
    }

    // Special features increase interest
    if (chocobo.hasSpeedXFactor || chocobo.hasStaminaXFactor) {
      interest *= 1.25
    }
    if (['gold', 'black'].includes(chocobo.color)) {
      interest *= 1.2
    }

    return interest
  }

  static calculateMaxBid(buyer, basePrice) {
    const maxPercentage = Math.min(0.8, 0.4 + (buyer.ambition / 200))
    const maxFromFunds = buyer.money * maxPercentage
    return Math.min(maxFromFunds, basePrice * 1.5)
  }

  static executeNPCTrade(auction, buyer, price, gameStore) {
    // Transfer money
    buyer.money -= price
    auction.owner.money += price

    // Transfer chocobo ownership
    const chocoboIndex = auction.owner.chocoboIds.indexOf(auction.chocobo.id)
    if (chocoboIndex !== -1) {
      auction.owner.chocoboIds.splice(chocoboIndex, 1)
    }
    buyer.chocoboIds.push(auction.chocobo.id)

    // Remove from active auctions
    gameStore.removeAuction(auction.chocobo.id)

    // Add to transaction history
    gameStore.addTransaction({
      seller: auction.owner,
      buyer,
      chocobo: auction.chocobo,
      price,
      date: gameStore.currentDay
    })
  }

  



  static generateNPCBids(chocobo, gameStore) {
    const baseValue = AuctionManager.calculateBaseValue(chocobo);
    const potentialBuyers = gameStore.owners.filter(owner => 
      !owner.isPlayer && 
      owner.money >= baseValue * 0.8 &&
      owner.chocoboIds.length < 15
    );
  
    return potentialBuyers
      .map(owner => {
        const bidMultiplier = this.calculateBidMultiplier(owner, chocobo);
        const amount = Math.round(baseValue * bidMultiplier);
        
        return {
          owner,
          amount,
          chocobo,
          interestLevel: bidMultiplier
        };
      })
      .filter(bid => bid.amount <= bid.owner.money * 0.8);
  }
  
  

  static calculateBidMultiplier(owner, chocobo) {
    let multiplier = 1.0

    // Adjust based on owner traits
    multiplier += (owner.ambition / 100) * 0.3
    multiplier += (owner.competitiveness / 100) * 0.2
    multiplier -= (owner.shrewdness / 100) * 0.1

    // Bonus for X-Factors
    if (chocobo.hasSpeedXFactor || chocobo.hasStaminaXFactor) {
      multiplier += 0.2
    }

    // Bonus for rare colors
    if (['gold', 'black'].includes(chocobo.color)) {
      multiplier += 0.15
    }

    // Bonus for high level chocobos
    if (chocobo.level >= 30) {
      multiplier += 0.25
    }

    return multiplier
  }

  static shouldNotifyAboutBid(bid, chocobo) {
    const baseValue = AuctionManager.calculateBaseValue(chocobo)
    return bid.amount >= baseValue * 1.1 && bid.interestLevel > 1.2
  }

  static generateNPCAuctions(gameStore) {
    const auctions = []
    const nonPlayerOwners = gameStore.owners.filter(owner => {
      // Explicitly check if this is the player owner
      if (owner.id === gameStore.playerOwner?.id) return false
      // Also check the isPlayer flag as a backup
      if (owner.isPlayer) return false
      return true
    })

    nonPlayerOwners.forEach(owner => {
      const ownedChocobos = gameStore.getChocobosByOwnerId(owner.id)
      
      if (ownedChocobos.length > 3) {
        const chocobosForAuction = this.selectChocobosForAuction(owner, ownedChocobos)
        
        chocobosForAuction.forEach(chocobo => {
          const baseValue = AuctionManager.calculateBaseValue(chocobo)
          auctions.push({
            owner,
            chocobo,
            basePrice: Math.round(baseValue * 1.2),
            urgent: owner.money < 10000,
            expiresOn: gameStore.currentDay + 3
          })
        })
      }
    })

    return auctions
  }

  static selectChocobosForAuction(owner, chocobos) {
    const scoredChocobos = chocobos.map(chocobo => ({
      chocobo,
      score: this.calculateKeepScore(chocobo, owner)
    }))

    scoredChocobos.sort((a, b) => a.score - b.score)

    const auctionCount = Math.max(1, Math.floor(chocobos.length * 0.2))
    return scoredChocobos
      .slice(0, auctionCount)
      .map(scored => scored.chocobo)
  }

  static calculateKeepScore(chocobo, owner) {
    let score = 0

    score += (chocobo.speed + chocobo.stamina) / 20
    score += chocobo.level * 2

    if (chocobo.hasSpeedXFactor) score += 20
    if (chocobo.hasStaminaXFactor) score += 20

    if (chocobo.color === 'gold') score += 30
    if (chocobo.color === 'black') score += 20

    score += (owner.ambition / 100) * 10
    score += (owner.competitiveness / 100) * 10

    const recentResults = chocobo.recentForm.filter(r => r !== null)
    if (recentResults.length > 0) {
      const avgPosition = recentResults.reduce((a, b) => a + b, 0) / recentResults.length
      score += (4 - avgPosition) * 5
    }

    if (owner.money < 10000) {
      score *= 0.7
    }

    return score
  }
}